/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * QrScanner
 * ---------
 * The camera half of the desk. Two separate jobs:
 *
 *   1. OPEN THE CAMERA — getUserMedia, the standard browser camera API. Works
 *      in every current browser on https:// or localhost, after the organizer
 *      allows it.
 *   2. READ THE QR in each frame — the browser's own BarcodeDetector where it
 *      exists (Chrome on Android, macOS, ChromeOS), otherwise jsQR, which does
 *      the same in plain JavaScript (Chrome/Edge on Windows and Linux, Safari
 *      and every iPhone browser, Firefox). jsQR is loaded only here and only
 *      when it is needed, so no other page pays for it.
 *
 * The camera only starts when the organizer taps Start — a page that opens the
 * camera on its own is a surprise, and a permission prompt nobody asked for.
 * The same code is not reported twice within SAME_CODE_QUIET_MS, and nothing is
 * reported while `paused` (a scan is still being checked).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';

const SCAN_EVERY_MS = 250;
// Long enough that a pass still held in front of the camera is not re-checked
// while the organizer reads the result or answers "Check in anyway?".
const SAME_CODE_QUIET_MS = 10000;
// Frames are shrunk to this width before jsQR reads them: plenty for a QR held
// up to the camera, and it keeps each read fast on slow phones.
const DECODE_WIDTH = 640;

export function cameraAvailable() {
  return typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);
}

/**
 * A function that reads a QR code out of the video's current frame, or null.
 * The built-in reader is used only when it really supports QR — some browsers
 * expose BarcodeDetector without any formats behind it.
 */
async function makeReader(video) {
  if ('BarcodeDetector' in window) {
    try {
      const formats = await window.BarcodeDetector.getSupportedFormats();
      if (formats.includes('qr_code')) {
        const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
        return async () => {
          const [code] = await detector.detect(video);
          return code?.rawValue || null;
        };
      }
    } catch (error) {
      // Fall through to jsQR.
    }
  }

  const { default: jsQR } = await import('jsqr');
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d', { willReadFrequently: true });
  return async () => {
    const scale = Math.min(1, DECODE_WIDTH / (video.videoWidth || DECODE_WIDTH));
    const width = Math.round((video.videoWidth || 0) * scale);
    const height = Math.round((video.videoHeight || 0) * scale);
    if (!width || !height) return null;
    canvas.width = width;
    canvas.height = height;
    context.drawImage(video, 0, 0, width, height);
    const { data } = context.getImageData(0, 0, width, height);
    return jsQR(data, width, height, { inversionAttempts: 'dontInvert' })?.data || null;
  };
}

function QrScanner({ onScan, paused }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const lastRef = useRef({ text: '', at: 0 });
  const pausedRef = useRef(paused);
  const onScanRef = useRef(onScan);
  const [state, setState] = useState('idle'); // idle | starting | on | denied | failed
  const supported = cameraAvailable();

  pausedRef.current = paused;
  onScanRef.current = onScan;

  const stop = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setState('idle');
  }, []);

  useEffect(() => stop, [stop]);

  const start = async () => {
    setState('starting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } }, audio: false,
      });
      streamRef.current = stream;
      const video = videoRef.current;
      video.srcObject = stream;
      await video.play();
      const read = await makeReader(video);
      let reading = false;
      timerRef.current = setInterval(async () => {
        if (reading || pausedRef.current || video.readyState < 2) return;
        reading = true;
        try {
          const text = (await read())?.trim();
          if (!text) return;
          const now = Date.now();
          if (text === lastRef.current.text && now - lastRef.current.at < SAME_CODE_QUIET_MS) return;
          lastRef.current = { text, at: now };
          onScanRef.current(text);
        } catch (error) {
          // A frame that fails to decode is normal; keep looking.
        } finally {
          reading = false;
        }
      }, SCAN_EVERY_MS);
      setState('on');
    } catch (error) {
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      setState(error?.name === 'NotAllowedError' ? 'denied' : 'failed');
    }
  };

  let message = null;
  if (!supported) {
    message = 'This browser can’t open the camera here (it needs https:// or localhost). Type the passcode below.';
  } else if (state === 'denied') {
    message = 'Camera access was blocked. Allow the camera for this site, or type the passcode below.';
  } else if (state === 'failed') {
    message = 'The camera couldn’t start. Type the passcode below, or try again.';
  }

  return (
    <div className={`ochk-cam${state === 'on' ? ' is-on' : ''}${paused ? ' is-paused' : ''}`}>
      <video ref={videoRef} className="ochk-video" muted playsInline aria-hidden="true" />
      {state === 'on' ? (
        <>
          <span className="ochk-frame" aria-hidden="true"><i /></span>
          <span className="ochk-laser" aria-hidden="true" />
          <button type="button" className="ochk-camstop" onClick={stop}>Stop camera</button>
          <span className="ochk-camhint">Point at any pass QR</span>
        </>
      ) : (
        <div className="ochk-camidle">
          <i className="bi bi-qr-code-scan" aria-hidden="true" />
          {message ? <p>{message}</p> : <p>Scan the QR on any participant’s pass.</p>}
          {supported && (
            <button type="button" className="org-btn" onClick={start} disabled={state === 'starting'}>
              {state === 'starting' ? 'Starting camera…' : 'Start camera'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default QrScanner;
